import { db, storage } from "../../../boot/firebase";
import {
  collection,
  deleteDoc,
  doc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytesResumable,
} from "firebase/storage";
import LibraryFile from "../../../classes/LibraryFile";
import LibraryFolder, {
  PERSONAL_LIBRARY_QUOTA_BYTES,
  personalLibraryUsageBytes,
} from "../../../classes/LibraryFolder";
import { i18n } from "../../../boot/i18n";
import { normalizeUploadImageFile } from "../../../util/imageUpload";
import { uid } from "quasar";

// One queue for the whole app. Every upload the Library page starts goes
// through it, so a dropped tree of forty folders still runs three uploads at
// a time rather than three PER folder, and the rows in state.libraryUploads
// outlive the page: leave the Library and come back, the card is still there.
const MAX_CONCURRENT_UPLOADS = 3;
// How long the finished rows linger once the whole queue is idle. While a
// batch is still running they stay put, so the card's "n of m uploaded"
// count means something; failed rows stay until the user clears them.
const DONE_ROW_LINGER_MS = 1500;
// The File objects behind the rows. They never enter the store: a File is
// not something to make reactive, and nothing but the scheduler needs it.
const uploadSources = new Map();
// Failed rows whose retry is still checking their folder, so a second click
// does not check (and prompt about) them twice.
const retryChecks = new Set();
let runningUploads = 0;
let leaveGuardArmed = false;

export const GET_LIBRARY_FILE = ({ dispatch }, id) => {
  return dispatch("GET_DOC", {
    converter: LibraryFile,
    path: LibraryFile.getPath(id),
    stateKey: "libraryFiles",
    id,
  });
};

export const GET_LIBRARY_FILES = (
  { dispatch },
  { where, limit, pagination } = {}
) => {
  return dispatch("GET_COLLECTION", {
    converter: LibraryFile,
    stateKey: "libraryFiles",
    path: LibraryFile.getPath(),
    where,
    limit,
    pagination,
  });
};

export const LISTEN_LIBRARY_FILE = (
  { dispatch },
  { id, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: LibraryFile,
    path: LibraryFile.getPath(id),
    stateKey: "libraryFiles",
    id,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_LIBRARY_FILES = (
  { dispatch },
  { where, listenerPath, next, error, unlisten } = {}
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: LibraryFile,
    path: LibraryFile.getPath(),
    stateKey: "libraryFiles",
    listenerPath,
    where,
    next,
    error,
    unlisten,
  });
};

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

// Build the storage path for a file. Tier is encoded in the prefix so the
// Storage security rules can enforce role access purely from the path. For
// the personal tier the owner's user ID is also encoded so the rules can
// pin reads/writes to that user only.
export function buildStoragePath({ tier, ownerID, storageID, name }) {
  // Keep original filename for friendlier downloads; prefix with a uid so the
  // same file can exist in multiple folders without collision.
  const safeName = String(name || "file").replace(/[^A-Za-z0-9._-]+/g, "_");
  if (tier === "personal") {
    if (!ownerID) throw "Missing ownerID for personal tier";
    return `library/personal/${ownerID}/${storageID}__${safeName}`;
  }
  return `library/${tier}/${storageID}__${safeName}`;
}

export function extensionOf(name) {
  if (!name) return "";
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : "";
}

// The folder a write targets, resolved from state when it is there. The page
// hands over plain `{ id, data }` shells for folders it created a moment ago
// (drag-dropped directory trees); those were created through the guarded
// SAVE_LIBRARY_FOLDER, so an unresolved folder is treated as writable.
function assertFolderWritable(state, folder) {
  const stored = folder && folder.id ? state.libraryFolders[folder.id] : null;
  if (!stored || stored.canBeEditedBy(state.user)) return;
  // "Read-only" only when read-only is the reason; a user without access to
  // the tier gets the generic refusal.
  if (stored.isReadOnly && stored.canBeViewedBy(state.user)) {
    throw "libraryFolderReadOnly";
  }
  throw "libraryNotPermitted";
}

function assertFileWritable(state, file) {
  if (!file || file.canBeEditedBy(state.user)) return;
  if (file.isReadOnly && file.canBeViewedBy(state.user)) {
    throw "libraryFolderReadOnly";
  }
  throw "libraryNotPermitted";
}

// Bytes of personal-tier uploads still in flight, per user. The queue runs
// three uploads at once and the store only learns about each file once its
// doc lands, so without this a batch could pass the quota check file by file
// and breach it in total.
const pendingPersonalBytes = new Map();

function reservePersonalBytes(userID, bytes) {
  pendingPersonalBytes.set(
    userID,
    (pendingPersonalBytes.get(userID) || 0) + bytes
  );
}

function releasePersonalBytes(userID, bytes) {
  const remaining = (pendingPersonalBytes.get(userID) || 0) - bytes;
  if (remaining > 0) pendingPersonalBytes.set(userID, remaining);
  else pendingPersonalBytes.delete(userID);
}

// ----------------------------------------------------------------------------
// Upload / Save
// ----------------------------------------------------------------------------

export const UPLOAD_LIBRARY_FILE = async (
  { dispatch, state },
  { file, folder, onProgress }
) => {
  if (!file) throw "No file";
  if (!folder || !folder.id) throw "Missing folder";
  assertFolderWritable(state, folder);

  const tier = folder.data.tier || "public";
  const normalized = await normalizeUploadImageFile(file);
  const incoming = normalized.size || file.size || 0;

  // Personal tier: enforce per-user storage cap before uploading, counting
  // the batch-mates still in flight (see pendingPersonalBytes).
  if (tier === "personal") {
    const usage =
      personalLibraryUsageBytes(state.libraryFiles, state.user.id) +
      (pendingPersonalBytes.get(state.user.id) || 0);
    if (usage + incoming > PERSONAL_LIBRARY_QUOTA_BYTES) {
      throw "personalLibraryQuotaExceeded";
    }
    reservePersonalBytes(state.user.id, incoming);
  }

  try {
    return await uploadReservedFile(
      { dispatch, state },
      {
        file,
        folder,
        onProgress,
        tier,
        normalized,
      }
    );
  } finally {
    if (tier === "personal") releasePersonalBytes(state.user.id, incoming);
  }
};

// The upload proper, after the quota reservation.
async function uploadReservedFile(
  { dispatch, state },
  { file, folder, onProgress, tier, normalized }
) {
  const docRef = doc(collection(db, LibraryFile.getPath()));
  const storagePath = buildStoragePath({
    tier,
    ownerID: state.user.id,
    storageID: docRef.id,
    name: normalized.name || file.name,
  });

  // Upload with progress reporting.
  const objectRef = storageRef(storage, storagePath);
  const metadata = {
    contentType: normalized.type || file.type || "application/octet-stream",
    customMetadata: {
      uploadedBy: state.user.id,
      folderID: folder.id,
      tier,
    },
  };

  await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(objectRef, normalized, metadata);
    task.on(
      "state_changed",
      (snapshot) => {
        if (typeof onProgress === "function" && snapshot.totalBytes) {
          onProgress(snapshot.bytesTransferred / snapshot.totalBytes);
        }
      },
      (err) => reject(err),
      () => resolve()
    );
  });

  const url = await getDownloadURL(objectRef);
  const now = new Date();
  const name = normalized.name || file.name;
  const data = {
    name,
    folderID: folder.id,
    tier,
    extension: extensionOf(name),
    size: normalized.size || file.size || 0,
    type: normalized.type || file.type || "",
    storagePath,
    url,
    description: "",
    created: now,
    createdBy: state.user.id,
    modified: now,
    modifiedBy: state.user.id,
    deletedAt: null,
    deletedBy: null,
  };

  await setDoc(docRef, data);
  dispatch("LOG_LIBRARY_ACTIVITY", {
    action: "file.upload",
    subjectType: "file",
    subjectID: docRef.id,
    subjectName: name,
    tier,
    details: { folderID: folder.id, size: data.size },
  });

  // Personal quota threshold toasts. The local listener will not have
  // observed the new doc yet, so include `incoming` explicitly.
  if (tier === "personal") {
    const before = personalLibraryUsageBytes(state.libraryFiles, state.user.id);
    const after = before + data.size;
    const cap = PERSONAL_LIBRARY_QUOTA_BYTES;
    const beforePct = (before / cap) * 100;
    const afterPct = (after / cap) * 100;
    if (beforePct < 100 && afterPct >= 100) {
      dispatch("NOTIFY_WARNING", "personalLibraryFull");
    } else if (beforePct < 80 && afterPct >= 80) {
      dispatch("NOTIFY_WARNING", "personalLibraryNearFull");
    }
  }
  return { id: docRef.id, ...data };
}

// A browser "leave this page?" prompt while anything is still uploading; a
// reload mid-batch would silently abandon the rest.
const warnBeforeUnload = (event) => {
  event.preventDefault();
  event.returnValue = "";
};

function hasActiveUploads(state) {
  return state.libraryUploads.some(
    (upload) => upload.status === "pending" || upload.status === "uploading"
  );
}

function syncLeaveGuard(state) {
  if (typeof window === "undefined") return;
  const active = hasActiveUploads(state);
  if (active && !leaveGuardArmed) {
    window.addEventListener("beforeunload", warnBeforeUnload);
    leaveGuardArmed = true;
  } else if (!active && leaveGuardArmed) {
    window.removeEventListener("beforeunload", warnBeforeUnload);
    leaveGuardArmed = false;
  }
}

function clearDoneRowsWhenIdle(context) {
  const { commit, state } = context;
  if (hasActiveUploads(state)) return;
  setTimeout(() => {
    if (hasActiveUploads(state)) return;
    state.libraryUploads
      .filter((upload) => upload.status === "done")
      .forEach((upload) => commit("REMOVE_LIBRARY_UPLOAD", upload.id));
  }, DONE_ROW_LINGER_MS);
}

// Starts uploads until MAX_CONCURRENT_UPLOADS are running; every finished
// upload calls it again, so the queue drains itself.
function pumpUploads(context) {
  const { commit, dispatch, state } = context;
  while (runningUploads < MAX_CONCURRENT_UPLOADS) {
    const next = state.libraryUploads.find(
      (upload) => upload.status === "pending"
    );
    if (!next) break;
    const source = uploadSources.get(next.id);
    if (!source) {
      commit("REMOVE_LIBRARY_UPLOAD", next.id);
      continue;
    }
    const id = next.id;
    runningUploads += 1;
    commit("UPDATE_LIBRARY_UPLOAD", { id, status: "uploading" });
    dispatch("UPLOAD_LIBRARY_FILE", {
      file: source.file,
      folder: source.folder,
      onProgress: (progress) =>
        commit("UPDATE_LIBRARY_UPLOAD", { id, progress }),
    })
      .then(() => {
        uploadSources.delete(id);
        commit("UPDATE_LIBRARY_UPLOAD", { id, status: "done", progress: 1 });
      })
      .catch((error) => {
        // The File stays in uploadSources so the row can be retried.
        console.error("Library upload failed", source.file.name, error);
        commit("UPDATE_LIBRARY_UPLOAD", { id, status: "error", error });
        dispatch("NOTIFY_ERROR", error);
      })
      .finally(() => {
        runningUploads -= 1;
        syncLeaveGuard(state);
        pumpUploads(context);
        clearDoneRowsWhenIdle(context);
      });
  }
}

// Queues `files` for upload into `folder` and returns their row ids. The
// page has already handled duplicates and permissions prompts; per-file
// failures surface as an error row plus a toast and never stop the rest.
export const QUEUE_LIBRARY_UPLOADS = (context, { files, folder }) => {
  if (!files || !files.length || !folder || !folder.id) return [];
  const items = Array.from(files).map((file) => {
    const id = uid();
    uploadSources.set(id, { file, folder });
    return {
      id,
      name: file.webkitRelativePath || file.name,
      folderID: folder.id,
      tier: folder.data.tier || "public",
      status: "pending",
      progress: 0,
      error: null,
    };
  });
  context.commit("ADD_LIBRARY_UPLOADS", items);
  syncLeaveGuard(context.state);
  pumpUploads(context);
  return items.map((item) => item.id);
};

// A folder's files as the server has them now, Trash left out. Throws "Not
// found" (GET_DOC) when the folder itself has been deleted. The fresh folder
// lands in the store, so the upload's read-only check sees today's flag.
async function liveFilesOfFolder(dispatch, folder) {
  await dispatch("GET_DOC", {
    converter: LibraryFolder,
    path: LibraryFolder.getPath(folder.id),
    stateKey: "libraryFolders",
    id: folder.id,
    forceUpdate: true,
  });
  const { files } = await dispatch("GET_LIBRARY_FOLDER_CONTENTS", {
    folder,
    folders: false,
  });
  return files.filter((file) => !file.isDeleted);
}

// Puts failed rows (all of them, or just `ids`) back in the queue. A retry
// can come long after the failure, so each row's folder is read afresh and
// gets the checks a first upload had: rows whose folder has been deleted
// since stay failed, with one toast naming them, and rows whose name has
// reached their folder since (the file was uploaded again by hand, say) are
// held back and returned as [{ id, existing }] for the page's duplicate
// prompt. A retried file starts over from the beginning: Firebase cannot
// pick a failed upload up where it stopped.
export const RETRY_LIBRARY_UPLOADS = async (context, ids) => {
  const { commit, dispatch, state } = context;
  const failed = () =>
    state.libraryUploads.filter(
      (upload) =>
        upload.status === "error" &&
        uploadSources.has(upload.id) &&
        !retryChecks.has(upload.id) &&
        (!ids || ids.includes(upload.id))
    );
  const rows = failed();
  if (!rows.length) return [];
  rows.forEach(({ id }) => retryChecks.add(id));

  const byFolder = new Map();
  rows.forEach((row) => {
    const { file, folder } = uploadSources.get(row.id);
    if (!byFolder.has(folder.id)) {
      byFolder.set(folder.id, { folder, uploads: [] });
    }
    byFolder.get(folder.id).uploads.push({ row, file });
  });

  const ready = new Set();
  const duplicates = [];
  const deleted = [];
  let failure = null;
  await Promise.all(
    [...byFolder.values()].map(async ({ folder, uploads }) => {
      let files;
      try {
        files = await liveFilesOfFolder(dispatch, folder);
      } catch (error) {
        if (error === "Not found") {
          deleted.push(...uploads.map(({ row }) => row.name));
        } else {
          failure = failure || error;
        }
        return;
      }
      uploads.forEach(({ row, file }) => {
        const wanted = (file.name || "").toLowerCase();
        const existing = files.find(
          (f) => (f.data.name || "").toLowerCase() === wanted
        );
        if (existing) duplicates.push({ id: row.id, existing });
        else ready.add(row.id);
      });
    })
  );
  rows.forEach(({ id }) => retryChecks.delete(id));

  if (failure) dispatch("NOTIFY_ERROR", failure);
  if (deleted.length) {
    dispatch(
      "NOTIFY_ERROR",
      i18n.global.tc("error.libraryRetryFolderDeleted", deleted.length, {
        count: deleted.length,
        names: deleted.join(", "),
      })
    );
  }

  const retry = failed().filter((upload) => ready.has(upload.id));
  retry.forEach(({ id }) =>
    commit("UPDATE_LIBRARY_UPLOAD", {
      id,
      status: "pending",
      progress: 0,
      error: null,
    })
  );
  if (retry.length) {
    syncLeaveGuard(state);
    pumpUploads(context);
  }
  return duplicates;
};

// Drops failed rows the user chose not to retry (the duplicate prompt's
// Skip), and lets their files go.
export const DISCARD_LIBRARY_UPLOADS = ({ commit, state }, ids) => {
  state.libraryUploads
    .filter((upload) => upload.status === "error" && ids.includes(upload.id))
    .forEach((upload) => {
      uploadSources.delete(upload.id);
      commit("REMOVE_LIBRARY_UPLOAD", upload.id);
    });
};

export const CLEAR_FINISHED_LIBRARY_UPLOADS = ({ commit, state }) => {
  // Failed rows still hold their File for a retry; clearing lets it go.
  state.libraryUploads
    .filter((upload) => upload.status === "error")
    .forEach((upload) => uploadSources.delete(upload.id));
  commit("CLEAR_FINISHED_LIBRARY_UPLOADS");
};

export const RENAME_LIBRARY_FILE = async (
  { dispatch, state },
  { id, name }
) => {
  if (!id || !name) throw "Missing id or name";
  const file = state.libraryFiles[id];
  if (!file) throw "libraryFileNotFound";
  assertFileWritable(state, file);
  const prev = file.data.name;
  await updateDoc(doc(db, LibraryFile.getPath(id)), {
    name,
    extension: extensionOf(name),
    modified: new Date(),
    modifiedBy: state.user.id,
  });
  dispatch("LOG_LIBRARY_ACTIVITY", {
    action: "file.rename",
    subjectType: "file",
    subjectID: id,
    subjectName: name,
    tier: file.data.tier,
    details: { from: prev, to: name },
  });
};

export const MOVE_LIBRARY_FILE = async (
  { dispatch, state },
  { id, folderID }
) => {
  // Note: moving between folders that share the same tier is a simple metadata
  // update. Moving across tiers would require copying the storage object under
  // a different path, so we reject that case.
  if (!id || !folderID) throw "Missing id or folderID";
  const file = state.libraryFiles[id];
  if (!file) throw "libraryFileNotFound";
  // Picked from the tree, so rarely loaded; its read-only flag is inherited
  // down the chain above it.
  await dispatch("ENSURE_LIBRARY_FOLDER_PATH", folderID).catch(() => undefined);
  const target = state.libraryFolders[folderID];
  if (!target) throw "libraryFolderNotFound";
  if (file.data.tier !== target.data.tier) {
    throw "libraryMoveCrossTier";
  }
  assertFileWritable(state, file);
  if (!target.canBeEditedBy(state.user)) throw "libraryFolderReadOnly";
  const fromFolderID = file.data.folderID || null;
  await updateDoc(doc(db, LibraryFile.getPath(id)), {
    folderID,
    modified: new Date(),
    modifiedBy: state.user.id,
  });
  dispatch("LOG_LIBRARY_ACTIVITY", {
    action: "file.move",
    subjectType: "file",
    subjectID: id,
    subjectName: file.data.name,
    tier: file.data.tier,
    details: { from: fromFolderID, to: folderID },
  });
};

// Soft-delete: the file is moved to Trash (deletedAt/deletedBy set) and the
// storage object is preserved. A scheduled Cloud Function permanently deletes
// both after the retention window.
export const DELETE_LIBRARY_FILE = async ({ dispatch, state }, id) => {
  if (!id) return;
  const file = state.libraryFiles[id];
  if (!file) throw "libraryFileNotFound";
  assertFileWritable(state, file);
  const now = new Date();
  await updateDoc(doc(db, LibraryFile.getPath(id)), {
    deletedAt: now,
    deletedBy: state.user.id,
    modified: now,
    modifiedBy: state.user.id,
  });
  dispatch("LOG_LIBRARY_ACTIVITY", {
    action: "file.delete",
    subjectType: "file",
    subjectID: id,
    subjectName: file.data.name,
    tier: file.data.tier,
    details: { folderID: file.data.folderID },
  });
};

// Restore a soft-deleted file (clears deletedAt/deletedBy). The Firestore
// rules enforce that only admins or the original deleter may do this.
export const RESTORE_LIBRARY_FILE = async ({ dispatch, state }, id) => {
  if (!id) return;
  const file = state.libraryFiles[id];
  if (!file) throw "libraryFileNotFound";
  if (!file.canBeRestoredBy(state.user)) throw "libraryNotPermitted";
  const now = new Date();
  // If the original folder was deleted while this file was in Trash, drop
  // the stale reference so the file restores to its tier root rather than
  // pointing at a folder that no longer exists. Not being loaded is not
  // being gone: ask before deciding.
  const folderID = file.data.folderID;
  let folderExists = Boolean(folderID && state.libraryFolders[folderID]);
  if (folderID && !folderExists) {
    folderExists = await dispatch("GET_LIBRARY_FOLDER", folderID).then(
      () => true,
      () => false
    );
  }
  const patch = {
    deletedAt: null,
    deletedBy: null,
    modified: now,
    modifiedBy: state.user.id,
  };
  if (folderID && !folderExists) {
    patch.folderID = null;
  }
  await updateDoc(doc(db, LibraryFile.getPath(id)), patch);
  dispatch("LOG_LIBRARY_ACTIVITY", {
    action: "file.restore",
    subjectType: "file",
    subjectID: id,
    subjectName: file.data.name,
    tier: file.data.tier,
  });
};

// Permanently delete a file (admin-only via Firestore rules). Removes the
// storage object then the Firestore doc. This is the same behavior the
// scheduled purge function performs server-side.
export const PURGE_LIBRARY_FILE = async ({ commit, dispatch, state }, id) => {
  if (!id) return;
  const file = state.libraryFiles[id];
  if (!file) throw "libraryFileNotFound";
  if (!file.canBePurgedBy(state.user)) throw "libraryNotPermitted";
  const storagePath = file.data.storagePath;
  const subjectName = file.data.name;
  const tier = file.data.tier;

  if (storagePath) {
    try {
      await deleteObject(storageRef(storage, storagePath));
    } catch (err) {
      if (err && err.code !== "storage/object-not-found") {
        console.error("Failed to delete library storage object", err);
      }
    }
  }

  await deleteDoc(doc(db, LibraryFile.getPath(id)));
  commit("REMOVE", { stateKey: "libraryFiles", id });
  dispatch("LOG_LIBRARY_ACTIVITY", {
    action: "file.purge",
    subjectType: "file",
    subjectID: id,
    subjectName,
    tier,
  });
};
